import { useCallback, useEffect } from 'react';
import { isElement, ForwardRefInstance, elementIsEqualActiveElement } from './utils';

export interface UseMobileCollapseParams<T extends HTMLElement> {
  ref: ForwardRefInstance<T>;
  collapse: () => void;
  expanded: boolean;
}

/**
 * @name useMobileCollapse
 * @description Хук для события скрытия виртуальной клавиатуры на ISO и Android
 * @param { UseMobileCollapseParams<T extends HTMLElement> }
 */
export const useMobileCollapse = <T extends HTMLElement = HTMLElement>({
  ref,
  collapse,
  expanded,
}: UseMobileCollapseParams<T>) => {
  // Обработка закрытия виртуальной клавиатуры на андроиде
  const onVirtualKeyboardCollapse = useCallback(
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    event => {
      const { height } = event.target.boundingRect;
      if (height === 0) {
        collapse();
      }
    },
    [collapse]
  );

  const current = isElement(ref) ? ref.current : null;

  // Обработка нажатия на конпку done виртуальной клавиатуры в сафари ISO
  const onIOSKeyboardCollapse = useCallback(
    event => {
      // Проверить не кликнуто ли на кнопку
      if (!event.relatedTarget && expanded) {
        collapse();
        /**
         * Фикс скрытия виртуально клавиатуры после скрытия инпута
         * Отрабатывает если клик был на произвольном месте экрана (не на ссылках и кнопках)
         */
        setTimeout(() => {
          // Если инпут все ещё активный элемент то вызвать blur
          if (elementIsEqualActiveElement(current)) {
            (document.activeElement as HTMLElement)?.blur();
          }
        }, 1);
      }
    },
    [collapse, current, expanded]
  );

  useEffect(() => {
    let element: T;

    if (isElement(ref)) {
      element = ref.current;
      element.addEventListener('focusout', onIOSKeyboardCollapse);
    }

    if (navigator && 'virtualKeyboard' in navigator) {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      navigator.virtualKeyboard.overlaysContent = true;
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      navigator.virtualKeyboard.addEventListener('geometrychange', onVirtualKeyboardCollapse);
    }

    return () => {
      element.removeEventListener('focusout', onIOSKeyboardCollapse);

      if (navigator && 'virtualKeyboard' in navigator) {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        navigator.virtualKeyboard.overlaysContent = false;
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        navigator.virtualKeyboard.removeEventListener('geometrychange', onVirtualKeyboardCollapse);
      }
    };
  }, [ref, collapse, onVirtualKeyboardCollapse, onIOSKeyboardCollapse]);
};
